import api from '../apiService';
import { API_ROUTES } from '../constants';
import { ActionExecutionDto, ActionScope, PlaybookCreateDto, PlaybookDto, PlaybookTemplate } from '../types';

export type { ActionExecutionDto, ActionScope, EntityRef, PlaybookActionDto, PlaybookCreateDto, PlaybookDto, TargetGroup } from '../types';

const API_BASE_URL = API_ROUTES.PLAYBOOKS;

export const playbookService = {
  getPlaybooks: async (params?: { isTriggerable?: boolean; entityType?: string; actionType?: string }) => {
    const response = await api.get<PlaybookDto[]>(API_BASE_URL, { params });
    return response.data;
  },

  getPlaybookById: async (id: string) => {
    const response = await api.get<PlaybookDto>(`${API_BASE_URL}/${id}`);
    return response.data;
  },

  createPlaybook: async (playbook: PlaybookCreateDto) => {
    const response = await api.post<PlaybookDto>(API_BASE_URL, playbook);
    return response.data;
  },

  updatePlaybook: async (id: string, playbook: Partial<PlaybookDto>) => {
    const response = await api.patch<PlaybookDto>(`${API_BASE_URL}/${id}`, playbook);
    return response.data;
  },

  deletePlaybook: async (id: string) => {
    await api.delete(`${API_BASE_URL}/${id}`);
  },

  // `selectedActions` is only sent when explicitly provided (checked via `!== undefined`, not truthiness,
  // so a deliberate empty array is still forwarded as an envelope body rather than silently downgrading
  // to the legacy shape), so existing callers (e.g. TriggerPlaybookButton.tsx's flat
  // { resource_type, resource_id, entity } body) keep posting the legacy shape the controller's
  // no-`selectedActions`-key branch expects.
  executePlaybook: async (playbookId: string, payload: Record<string, unknown>, selectedActions?: ActionScope[]) => {
    const body = selectedActions !== undefined ? { payload, selectedActions } : payload;
    const response = await api.post(`${API_BASE_URL}/${playbookId}/execute`, body);
    return response.data;
  },

  getExecutionActions: async (playbookId: string, executionId: string) => {
    const response = await api.get<ActionExecutionDto[]>(`${API_BASE_URL}/${playbookId}/executions/${executionId}/actions`);
    return response.data;
  },

  getExecutionsHistory: async (id: string, page: number = 1, perPage: number = 50) => {
    const response = await api.get<any[]>(`${API_BASE_URL}/${id}/${API_ROUTES.EXECUTION_HISTORY}?page=${page}&per_page=${perPage}`);
    return response.data ?? [];
  },

  getPlaybookAudiLogs: async (playbookId: string) => {
    const response = await api.get(`${API_BASE_URL}/${playbookId}/history`);
    return response.data;
  },

  getPlaybookTemplates: async (): Promise<PlaybookTemplate[]> => {
    const response = await api.get<PlaybookTemplate[]>(`${API_BASE_URL}/templates`);
    return response.data;
  },

  getPlaybookTemplateById: async (templateId: string): Promise<PlaybookTemplate> => {
    const response = await api.get<PlaybookTemplate>(`${API_BASE_URL}/templates/${templateId}`);
    return response.data;
  },
};
